import { useRef, useEffect, useState, useCallback } from "react";
import CCMessageItem, { CCStreamingText, CCStreamingThinking } from "./CCMessageItem";
import CCQuestionCard from "./CCQuestionCard";
import CCRosterBar from "./CCRosterBar";

/**
 * CC 智能实验员交互主面板（设计方案 §7.2 / §7.3）
 *
 * 替代原有"▶ Claw 实验机终端"只读面板，提供：
 *   - 顶部状态条（ccStatus + ccSessionState 指示灯）
 *   - 消息列表（自动滚底）
 *   - 底部输入框（发送消息 → ws → GoMAown → CC）
 *
 * Props:
 *   ccMessages       消息数组（来自 useCCState）
 *   ccStreamingText  当前流式文本
 *   ccSessionState   idle / running / requires_action
 *   ccStatus         状态条文本
 *   ccSelectedDevice { device_id, device_name }
 *   onSendMessage    (text) => void  发送消息回调
 *   onInterrupt      () => void      中断按钮回调
 */
const SESSION_STATE_COLORS = {
  idle: "#3fb950",
  running: "#f0883e",
  requires_action: "#d29922",
};

const SESSION_STATE_LABELS = {
  idle: "空闲",
  running: "运行中",
  requires_action: "等待审批",
};

// permissionMode 显示配置（default 不显示）
const PERMISSION_MODE_DISPLAY = {
  bypassPermissions: { label: "跳过权限", color: "#7d4a1a", bg: "#1e1208" },
  dontAsk:          { label: "不询问",   color: "#7d4a1a", bg: "#1e1208" },
  plan:             { label: "规划模式", color: "#4a4a5a", bg: "#16161e" },
  acceptEdits:      { label: "自动接受编辑", color: "#4a4a5a", bg: "#16161e" },
};


const CCPanel = ({
  ccMessages,
  ccStreamingText,
  ccThinkingText,
  ccSessionState,
  ccStatus,
  ccSelectedDevice,
  ccInitInfo,
  ccPendingQuestion,
  ccRoster,
  onSendMessage,
  onInterrupt,
  onQuestionRespond,
  onWorkspaceFileOpen,
  bottomControlBar,
}) => {
  const listRef = useRef(null);
  const stickToBottom = useRef(true);
  const [inputText, setInputText] = useState("");
  const [showJumpLatest, setShowJumpLatest] = useState(false);

  // 认知协同 / 行动协同 双回路指示：存在"研讨中"的子智能体回合 → 认知协同回路激活
  const inDiscussion = (ccMessages || []).some(
    (m) => m.type === "agent_turn" && m.status !== "done"
  );

  // 把快捷追问写入输入框（学生确认后再发，不自动发送）
  const handleInsertPrompt = useCallback((text) => {
    if (!text) return;
    setInputText((prev) => (prev && prev.trim() ? `${prev.trim()} ${text}` : text));
  }, []);

  const jumpToLatest = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    stickToBottom.current = true;
    setShowJumpLatest(false);
  }, []);

  const handleListScroll = () => {
    const el = listRef.current;
    if (!el) return;
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    stickToBottom.current = dist < 48;
    setShowJumpLatest(!stickToBottom.current);
  };

  useEffect(() => {
    if (stickToBottom.current && listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [ccMessages, ccStreamingText, ccThinkingText]);

  const handleSend = useCallback(() => {
    const text = inputText.trim();
    if (!text || ccSessionState === "running") return;
    onSendMessage(text);
    setInputText("");
  }, [inputText, ccSessionState, onSendMessage]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const path = e.dataTransfer.getData("application/x-file-path") || e.dataTransfer.getData("text/plain");
    if (!path) return;
    setInputText((prev) => `${prev}${prev && !prev.endsWith(" ") ? " " : ""}@${path} `);
  };

  const stateColor = SESSION_STATE_COLORS[ccSessionState] || "#8b949e";
  const stateLabel = SESSION_STATE_LABELS[ccSessionState] || ccSessionState;
  const isRunning = ccSessionState === "running";

  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      height: "100%",
      width: "100%",
      minHeight: 0,
      minWidth: 0,
      overflow: "hidden",
      background: "#0d1117",
      color: "#e6edf3",
      fontFamily: "system-ui, -apple-system, sans-serif",
    }}>
      {/* ── 顶部状态条 ─────────────────────────────────────────────── */}
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "6px 14px",
        borderBottom: "1px solid #21262d",
        fontSize: "12px",
        flexShrink: 0,
        minWidth: 0,
        overflow: "hidden",
      }}>
        <span style={{ color: "#58a6ff", fontFamily: "'Courier New', monospace", flexShrink: 0 }}>▶ YouLabber 智能实验员</span>
        {ccSelectedDevice && (
          <span style={{ color: "#8b949e", flexShrink: 0 }}>— {ccSelectedDevice.device_name}</span>
        )}

        {/* cwd 工作目录（截取最后一段，hover 显示完整路径） */}
        {ccInitInfo?.cwd && (
          <span
            title={ccInitInfo.cwd}
            style={{
              color: "#484f58",
              fontSize: "11px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "160px",
              flexShrink: 1,
            }}
          >
            {ccInitInfo.cwd.replace(/\\/g, "/").split("/").filter(Boolean).pop() || ccInitInfo.cwd}
          </span>
        )}

        {/* permissionMode 标签（default 不显示） */}
        {ccInitInfo?.permissionMode && PERMISSION_MODE_DISPLAY[ccInitInfo.permissionMode] && (() => {
          const cfg = PERMISSION_MODE_DISPLAY[ccInitInfo.permissionMode];
          return (
            <span style={{
              fontSize: "10px",
              color: cfg.color,
              background: cfg.bg,
              border: `1px solid ${cfg.color}44`,
              borderRadius: "3px",
              padding: "0 5px",
              lineHeight: "16px",
              flexShrink: 0,
            }}>
              {cfg.label}
            </span>
          );
        })()}

        <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "5px", flexShrink: 0 }}>
          {/* 双回路指示：认知协同（研讨中）/ 行动协同（操作设备） */}
          <span
            title={inDiscussion ? "正在与研讨角色进行认知协同" : "与实验员进行行动协同（操作设备/文件）"}
            style={{
              fontSize: "10px",
              color: inDiscussion ? "#d29922" : "#3fb950",
              border: `1px solid ${inDiscussion ? "#d29922" : "#3fb950"}55`,
              background: inDiscussion ? "#d2992218" : "#3fb95018",
              borderRadius: "3px",
              padding: "0 6px",
              lineHeight: "16px",
              marginRight: "4px",
            }}
          >
            {inDiscussion ? "🧠 认知协同" : "🛠 行动协同"}
          </span>
          <span style={{
            width: "7px", height: "7px", borderRadius: "50%",
            background: stateColor, display: "inline-block",
            boxShadow: isRunning ? `0 0 6px ${stateColor}` : "none",
          }} />
          <span style={{ color: stateColor }}>{stateLabel}</span>
          {ccStatus && <span style={{ color: "#6e7681", marginLeft: "6px" }}>· {ccStatus}</span>}
        </span>
        {isRunning && (
          <button
            onClick={onInterrupt}
            title="中断"
            style={{
              background: "none", border: "1px solid #f85149",
              color: "#f85149", borderRadius: "4px",
              padding: "1px 8px", cursor: "pointer", fontSize: "11px",
              flexShrink: 0,
            }}
          >
            ⏹ 中断
          </button>
        )}
      </div>

      {/* ── 研讨角色名册（认知协同）───────────────────────────────────── */}
      <CCRosterBar roster={ccRoster} />

      {/* ── 消息列表 ────────────────────────────────────────────────── */}
      <div style={{ flex: 1, minHeight: 0, minWidth: 0, position: "relative" }}>
      <div
        ref={listRef}
        onScroll={handleListScroll}
        style={{
          height: "100%",
          overflowX: "hidden",
          overflowY: "auto",
          padding: "10px 14px",
          display: "flex",
          flexDirection: "column",
          gap: "2px",
        }}
      >
        {ccMessages.length === 0 && !ccStreamingText && (
          <div style={{ color: "#6e7681", fontSize: "13px", textAlign: "center", marginTop: "40px" }}>
            向 CC 发送消息开始对话
          </div>
        )}
        {ccMessages.map((msg) => (
          <div key={msg.id} style={{ flexShrink: 0 }}>
            <CCMessageItem
              msg={msg}
              onWorkspaceFileOpen={onWorkspaceFileOpen}
              roster={ccRoster}
              onInsertPrompt={handleInsertPrompt}
            />
          </div>
        ))}
        {ccThinkingText && <div style={{ flexShrink: 0 }}><CCStreamingThinking text={ccThinkingText} /></div>}
        {ccStreamingText && <div style={{ flexShrink: 0 }}><CCStreamingText text={ccStreamingText} /></div>}
        {ccPendingQuestion && (
          <div style={{ flexShrink: 0 }}>
            <CCQuestionCard
              pendingQuestion={ccPendingQuestion}
              onRespond={onQuestionRespond}
            />
          </div>
        )}
      </div>
      {showJumpLatest && (
        <button
          type="button"
          onClick={jumpToLatest}
          style={{
            position: "absolute",
            right: 16,
            bottom: 10,
            border: "1px solid #30363d",
            background: "#161b22",
            color: "#58a6ff",
            borderRadius: 14,
            padding: "4px 10px",
            fontSize: 12,
            cursor: "pointer",
          }}
        >
          回到最新
        </button>
      )}
      </div>

      {/* ── 底部输入框 ──────────────────────────────────────────────── */}
      <div style={{
        borderTop: "1px solid #21262d",
        padding: "8px 12px",
        display: "flex",
        gap: "8px",
        alignItems: "flex-end",
        flexShrink: 0,
      }}
      onDragOver={(e) => e.preventDefault()}
      onDrop={handleDrop}
      >
        <textarea
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={isRunning ? "YouLabber 运行中，请等待..." : "发送消息给邮百工实验员 YouLabber（Enter 发送，Shift+Enter 换行）"}
          disabled={isRunning}
          rows={2}
          style={{
            flex: 1,
            background: "#161b22",
            border: "1px solid #30363d",
            borderRadius: "6px",
            color: "#e6edf3",
            padding: "8px 10px",
            fontSize: "13px",
            resize: "none",
            outline: "none",
            fontFamily: "inherit",
            opacity: isRunning ? 0.5 : 1,
          }}
        />
        <button
          onClick={handleSend}
          disabled={isRunning || !inputText.trim()}
          style={{
            background: isRunning || !inputText.trim() ? "#21262d" : "#238636",
            border: "none",
            borderRadius: "6px",
            color: "#e6edf3",
            padding: "8px 14px",
            cursor: isRunning || !inputText.trim() ? "not-allowed" : "pointer",
            fontSize: "13px",
            height: "fit-content",
            alignSelf: "flex-end",
          }}
        >
          发送
        </button>
      </div>
      {bottomControlBar}
    </div>
  );
};

export default CCPanel;
